/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { Drawer, Input, Card, Tag, Typography, Spin, Empty } from 'antd';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import moment from 'moment';
import {
  getOfferFinalizationRecipients,
  getOfferReopenRecipients,
} from '~src/services/api/notifications';

interface OfferNotificationRecipientsDrawerProps {
  visible: boolean;
  onClose: () => void;
  type: 'finalize' | 'reopen';
  projectId: number;
  tenderId: number;
}

const getStateColor = (state: number): string => {
  const colors: Record<number, string> = {
    1: 'default', // pending
    2: 'processing', // sent
    3: 'success', // delivered
    4: 'error', // failed
  };
  return colors[state] || 'default';
};

const getStateText = (state: number, intl: any): string => {
  const keys: Record<number, string> = {
    1: 'notification-state-pending',
    2: 'notification-state-sent',
    3: 'notification-state-delivered',
    4: 'notification-state-failed',
  };
  return intl.formatMessage({ id: keys[state] || 'unknown' });
};

export const OfferNotificationRecipientsDrawer: React.FC<OfferNotificationRecipientsDrawerProps> = ({
  visible,
  onClose,
  type,
  projectId,
  tenderId,
}) => {
  const intl = useIntl();
  const [searchText, setSearchText] = useState('');

  const isFinalize = type === 'finalize';
  const getRecipientsFn = isFinalize
    ? getOfferFinalizationRecipients
    : getOfferReopenRecipients;

  const { data: recipients, isLoading, error } = useQuery(
    ['offer-notification-recipients', type, projectId, tenderId],
    () =>
      getRecipientsFn({
        projectId,
        tenderId,
        skip: 0,
        take: 100,
      }),
    {
      enabled: visible && !!projectId && !!tenderId,
      retry: false, // Don't retry on 404
      onError: (err: any) => {
        // Don't crash page on 404 - recipients might not exist yet
        console.warn('Recipients load failed:', err);
      },
    }
  );

  // IMPORTANT: recipients is an array, not { items, totalCount }
  const filteredRecipients = (recipients || []).filter((r) =>
    searchText
      ? r.email.toLowerCase().includes(searchText.toLowerCase()) ||
        r.share_key_title.toLowerCase().includes(searchText.toLowerCase())
      : true
  );

  return (
    <Drawer
      title={intl.formatMessage({ id: 'offer-notification-recipients-title' })}
      visible={visible}
      onClose={onClose}
      width={600}>
      <Input.Search
        placeholder={intl.formatMessage({ id: 'search' })}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />

      {isLoading && (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      )}

      {!isLoading && error && (
        <Empty
          description={intl.formatMessage({
            id: 'notification-recipients-load-failed',
          })}
        />
      )}

      {!isLoading && !error && filteredRecipients.length === 0 && (
        <Empty
          description={intl.formatMessage({ id: 'no-recipients-found' })}
        />
      )}

      {!isLoading &&
        !error &&
        filteredRecipients.map((recipient) => (
          <Card key={recipient.id} style={{ marginBottom: 12 }} size="small">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}>
              <Typography.Text strong>
                {recipient.share_key_title}
              </Typography.Text>
              <Tag color={getStateColor(recipient.state)}>
                {getStateText(recipient.state, intl)}
              </Tag>
            </div>

            <Typography.Text
              type="secondary"
              style={{ display: 'block', marginBottom: 4 }}>
              {recipient.email}
            </Typography.Text>

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {intl.formatMessage({ id: 'sent-at' })}:{' '}
              {recipient.mailed_at
                ? moment.unix(recipient.mailed_at).format('DD.MM.YYYY HH:mm')
                : '-'}
            </Typography.Text>

            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginLeft: 16 }}>
              {intl.formatMessage({ id: 'language' })}:{' '}
              {recipient.recipient_email_language_code}
            </Typography.Text>
          </Card>
        ))}

      {!isLoading && recipients && (
        <Typography.Text
          type="secondary"
          style={{ marginTop: 16, display: 'block' }}>
          {intl.formatMessage({ id: 'showing-recipients' })}:{' '}
          {filteredRecipients.length} / {recipients.length}
        </Typography.Text>
      )}
    </Drawer>
  );
};
